<script lang="ts">
	import Katex from './Katex.svelte';
	import uqnSplit from './functions/uqnSplit';
	import signAssign from './functions/signAssign';
	import fractionBuilder from './functions/fractionBuilder';
	import Fraction from './functions/fractionClass';
	import polyBuilder from './functions/polyBuilder';
	import Surd from './functions/surdClass';
	import gcd from './functions/gcd';

	export let uqn: number;
	let general: string, particular: string, infinity: string;
	$: [general, particular, infinity] = uqnToQn(uqn);

	function uqnToQn(u:number):string[]{
		let inf = "";
		let [t,i,a,b,c,s] = uqnSplit(u);
		let g: string, p: string;
		if (t==1){ // f(x)
			if (i<4){ // perfect square cases
				if (i==0){
					let bOverA = new Fraction(b,a).simplify().toString();
					bOverA = bOverA == "1"? "" : bOverA;
					let xTerm = a==1? "x" : `\\frac{x}{${a.toString()}}`;
					g = `${bOverA} \\tan^{-1} \\left ( ${xTerm} \\right ) + C.`
					p = `${bOverA} \\tan^{-1} \\left ( ${xTerm} \\right ) + ${c.toString()}.`
					inf = new Fraction(b,2*a).simplify().toString() + "\\pi +" + c.toString() + ".";
				} else if (i==1){
					let bOverA = new Fraction(b,2*a).simplify().toString();
					bOverA = bOverA == "1"? "" : bOverA;
					let xTerm = `\\frac{x-${a.toString()}}{x+${a.toString()}}`;
					g = `${bOverA} \\ln \\left | ${xTerm} \\right | + C.`
					p = `${bOverA} \\ln \\left | ${xTerm} \\right | + ${c.toString()}.`
				} else{ // f'(x) cases
					let signKatex = i==2? "+" : "-";
					let bOverA = new Fraction(b,2).simplify().toString();
					bOverA = bOverA == "1"? "" : bOverA;
					let xTerm = `x^2 ${signKatex} ${(a*a).toString()}`;
					g = `${bOverA} \\ln \\left | ${xTerm} \\right | + C.`
					p = `${bOverA} \\ln \\left | ${xTerm} \\right | + ${c.toString()}.`
				}
			} else if (i==4){ // complete square case
				[a,b] = signAssign(s,a,b);
				let newA = 4*b-a*a;
				let surd = new Surd(1,Math.abs(newA));
				let minusA: string, plusA: string, x: string;
				if (surd.surd==1){ // not a surd
					if ( (a-Number(surd.coeff))%2==0 ) { // divisible by 2: "x"
						x = "x";
						minusA = ((a-Number(surd.coeff))/2).toString();
						plusA = ((a+Number(surd.coeff))/2).toString();
					} else{ // not divisible by 2: "2x"
						x = "2x";
						minusA = ((a-Number(surd.coeff))).toString();
						plusA = ((a+Number(surd.coeff))).toString();
					}
					// add + signs if necessary
					if (a-Number(surd.coeff)>0){
						minusA = "+" + minusA;
					}
					if (a+Number(surd.coeff)>0){
						plusA = "+" + plusA;
					}
				} else{ // surd form
					let a2: number, surd2: Surd;
					if (a%2==0 && Number(surd.coeff)%2==0){
						x = "x";
						a2 = a/2;
						surd2 = new Surd(Number(surd.coeff)/2,surd.surd);
					} else{
						x = "2x";
						a2 = a;
						surd2 = new Surd(surd.coeff, surd.surd);
					}
					minusA = `${a2.toString()}-${surd2.toString()}`;
					plusA = `${a2.toString()}+${surd2.toString()}`;
					if (a>0){
						minusA = "+" + minusA;
						plusA = "+" + plusA;
					}
				}
				let num = newA > 0? polyBuilder([2,a],"x",1,false): `${x} ${minusA}`;
				let den = newA > 0? surd.toString(): `${x} ${plusA}`;
				let oneOverA: string;
				if (Math.abs(newA)%4==0){
					oneOverA = fractionBuilder(1,new Surd(1,Math.abs(newA)/4).toString());
					if (newA > 0 && Math.abs(a)%2==0){
						num = polyBuilder([1,a/2],"x",1,false);
						den = new Surd(1,Math.abs(newA/4)).toString();
					}
				} else if (Math.abs(newA)%2==0){ // theoretically impossible
					oneOverA = new Surd(1,new Fraction(Math.abs(newA),4)).toString();
				} else{
					oneOverA = fractionBuilder(2,new Surd(1,Math.abs(newA)).toString());
				}
				let surdDenom = surd.toString();
				let oneOverTwoA = surdDenom == "1"? "" : fractionBuilder(1,surdDenom);
				oneOverA = oneOverA == "1"? "" : oneOverA;
				g = newA > 0? `${oneOverA} \\tan^{-1} \\left ( ${fractionBuilder(num,den)} \\right ) + C.` : `${oneOverTwoA} \\ln \\left | ${fractionBuilder(num,den)} \\right | + C.`;
				p = newA > 0? `${oneOverA} \\tan^{-1} \\left ( ${fractionBuilder(num,den)} \\right ) + ${c.toString()}.` : `${oneOverTwoA} \\ln \\left | ${fractionBuilder(num,den)} \\right | + ${c.toString()}.`;
			} else if (i==5){
				g = `${fractionBuilder(`\\sin ${(2*a).toString()} x`,(4*a).toString())} + \\frac{x}{2} + C.`;
				p = `${fractionBuilder(`\\sin ${(2*a).toString()} x`,(4*a).toString())} + \\frac{x}{2} + ${c.toString()}.`;
			} else if (i==6){
				g = `\\frac{x}{2} - ${fractionBuilder(`\\sin ${(2*a).toString()} x`,(4*a).toString())} + C.`;
				p = `\\frac{x}{2} - ${fractionBuilder(`\\sin ${(2*a).toString()} x`,(4*a).toString())} + ${c.toString()}.`;
			}
			g = "y="+g;
			p = "y="+p;
		} else if (t==2){ // f(y)
			if (i==0){ // exp answer
				let expCoeff = new Fraction(-c,b).simplify().toString();
				expCoeff = expCoeff == "-1"? "-" : expCoeff;
				g = b==1? `${a.toString()}-Ae^{${polyBuilder([-b],"x",1)}}.` : fractionBuilder(`${a.toString()}-Ae^{${polyBuilder([-b],"x",1)}}`,b.toString())+".";
				let aOverB = new Fraction(a,b).simplify().toString();
				p = `${aOverB + expCoeff} e^{ ${polyBuilder([-b],"x",1)} }.`
				inf = aOverB + ".";
				g = "\\displaystyle y="+g;
				p = "\\displaystyle y="+p;
			} else if (i<3){ // 1 or 2: sqrt, cube root
				[a,b] = signAssign(s,a,b);
				let expCoeff = new Fraction(i+1,b).simplify().toString();
				if (expCoeff == "1"){
					expCoeff = "";
				} else if (expCoeff == "-1"){
					expCoeff = "-"
				}
				let pRoot = i==1? "" : "[3]"
				g = `x = ${expCoeff}\\sqrt${pRoot}{ ${polyBuilder([a,b],"y")} } +C.`;
				p = `x = ${expCoeff}\\sqrt${pRoot}{ ${polyBuilder([a,b],"y")} } - ${c.toString()}.`;				
			} else if (i==3){ // a^2 + b^2 y^2
				let oneOverAB = typesetSign(new Fraction(1,a*b).simplify().toString());
				let bOverA = typesetSign(new Fraction(b,a).simplify().toString());
				g = `\\displaystyle x = ${oneOverAB} \\tan^{-1} \\left ( ${bOverA}y \\right ) + C.`;
				p = `\\displaystyle x = ${oneOverAB} \\tan^{-1} \\left ( ${bOverA}y \\right ) + ${c.toString()}.`;
			} else if (i==4){ // a^2 - b^2 y^2
				let oneOverAB = typesetSign(new Fraction(1,2*a*b).simplify().toString());
				let divisor = gcd(a,b);
				let a2 = a/divisor, b2 = b/divisor;
				g = `\\displaystyle x = ${oneOverAB} \\ln \\left | \\frac{${polyBuilder([a2,b2],"y")}}{${polyBuilder([a2,-b2],"y")}} \\right | + C.`;
				p = `\\displaystyle x = ${oneOverAB} \\ln \\left | \\frac{${polyBuilder([a2,b2],"y")}}{${polyBuilder([a2,-b2],"y")}} \\right | + ${c.toString()}.`;
			} else if (i==5){ // complete square - (x^2 + ax + b)
				[a,b] = signAssign(s,a,b);
				let newA = 4*b-a*a;
				let surd = new Surd(1,Math.abs(newA));
				let minusA: string, plusA: string, x: string;
				if (surd.surd==1){ // not a surd
					if ( (a-Number(surd.coeff))%2==0 ) { // divisible by 2: "x"
						x = "y";
						minusA = ((a-Number(surd.coeff))/2).toString();
						plusA = ((a+Number(surd.coeff))/2).toString();
					} else{ // not divisible by 2: "2x"
						x = "2y";
						minusA = ((a-Number(surd.coeff))).toString();
						plusA = ((a+Number(surd.coeff))).toString();
					}
					// add + signs if necessary
					if (a-Number(surd.coeff)>0){
						minusA = "+" + minusA;
					}
					if (a+Number(surd.coeff)>0){
						plusA = "+" + plusA;
					}
				} else{ // surd form
					let a2: number, surd2: Surd;
					if (a%2==0 && Number(surd.coeff)%2==0){
						x = "y";
						a2 = a/2;
						surd2 = new Surd(Number(surd.coeff)/2,surd.surd);
					} else{
						x = "2y";
						a2 = a;
						surd2 = new Surd(surd.coeff, surd.surd);
					}
					minusA = `${a2.toString()}-${surd2.toString()}`;
					plusA = `${a2.toString()}+${surd2.toString()}`;
					if (a>0){
						minusA = "+" + minusA;
						plusA = "+" + plusA;
					}
				}
				let num = newA > 0? polyBuilder([2,a],"y",1,false): `${x} ${minusA}`;
				let den = newA > 0? surd.toString(): `${x} ${plusA}`;
				let oneOverA: string;
				if (Math.abs(newA)%4==0){
					if (Math.abs(newA)==4){
						oneOverA = (Math.abs(newA)/4).toString();
					} else{
						oneOverA = fractionBuilder(1,new Surd(1,Math.abs(newA)/4).toString());
					}
					if (newA > 0 && Math.abs(a)%2==0){
						num = polyBuilder([1,a/2],"y",1,false);
						den = new Surd(1,Math.abs(newA/4)).toString();
					}
				} else if (Math.abs(newA)%2==0){ // theoretically impossible
					oneOverA = new Surd(1,new Fraction(Math.abs(newA),4)).toString();
				} else{
					oneOverA = fractionBuilder(2,new Surd(1,Math.abs(newA)).toString());
				}
				let fractionString = den=="1"? num : fractionBuilder(num,den);
				let fractionString2 = fractionBuilder(den,num);
				let surdDenom = surd.toString();
				let oneOverTwoA = surdDenom == "1"? "" : fractionBuilder(1,surdDenom);
				oneOverA = oneOverA == "1"? "" : oneOverA;
				g = newA > 0? `-${oneOverA} \\tan^{-1} \\left ( ${fractionString} \\right ) + C.` : `${oneOverTwoA} \\ln \\left | ${fractionString2} \\right | + C.`;
				p = newA > 0? `-${oneOverA} \\tan^{-1} \\left ( ${fractionString} \\right ) + ${c.toString()}.` : `${oneOverTwoA} \\ln \\left | ${fractionString2} \\right | + ${c.toString()}.`;
				g = "x=" + g;
				p = "x=" + p;
			} else{ // e^(ay)
				let oneOverA = typesetSign(new Fraction(1,a).toString());
				g = `\\displaystyle x=- ${oneOverA} e^{ ${polyBuilder([-a],"y",1)} } + C.`;
				p = a==1? `\\displaystyle x=${`${c.toString()} - e^{ ${polyBuilder([-a],"y",1)} }`}` : `\\displaystyle x=${fractionBuilder( `${c.toString()} - e^{ ${polyBuilder([-a],"y",1)} }`, a.toString())}`;
			}
		} else if (t==3){ // d2ydx2 = f(x)
			[a,b,c] = signAssign(s,a,b,c);
			if (i==0){
				let aFrac = new Fraction(a,2).simplify();
				g = polyBuilder([aFrac, 'b', 'c'],'x',2,false)+'.';
				p = polyBuilder([aFrac, b, c],'x',2,false)+'.';
			} else if (i==1){
				let aFrac = new Fraction(a,2).simplify();
				let bFrac = new Fraction(b,6).simplify()
				g = polyBuilder([bFrac, aFrac, 'c', 'd'],'x',3,false)+'.';
				p = polyBuilder([bFrac, aFrac, c, 1],'x',3,false)+'.';
			} else if (i==2){
				let aFrac = typesetSign(new Fraction(1,a*a).toString());
				let polyString1 = polyBuilder(['b','c'],'x',1,false);
				let polyString2 = polyBuilder([b,c],'x',1,false);
				polyString2 = b>0? '+'+polyString2 : polyString2;
				g = `${aFrac}e^{ ${polyBuilder([a],"x",1)} } + ${polyString1}.`;
				p = `${aFrac}e^{ ${polyBuilder([a],"x",1)} } ${polyString2}.`;
			} else{
				const funcString = i==3? "\\cos " : "\\sin "
				let aFrac = typesetSign(new Fraction(-1,a*a).toString());
				let polyString1 = polyBuilder(['b','c'],'x',1,false);
				let polyString2 = polyBuilder([b,c],'x',1,false);
				polyString2 = b>0? '+'+ polyString2 : polyString2;
				g = `${aFrac+funcString} ( ${polyBuilder([a],"x",1)} ) + ${polyString1}.`;
				p = `${aFrac+funcString} ( ${polyBuilder([a],"x",1)} ) ${polyString2}.`;
			}
			g = "y="+g;
			p = "y="+p;
		}
		return [g,p,inf];
	}
	function typesetSign(s:string):string{
		if (s=="1"){
			return ""
		} else if (s=="-1"){
			return "-"
		} else{
			return s;
		}
	}
</script>

<p> 
	(a) <Katex math={general}/>
</p>
<p>
	(b) <Katex math={particular}/>
</p>
{#if infinity}
<p>(c) <Katex math={infinity}/></p>
{/if}

